import { observable, action, flow, makeObservable } from 'mobx';
import { TamariskDocumentStore } from './tamariskDocumentStore';
import { AppraisalRequest } from './../types/AppraisalRequest';
import { LoanTermsService } from '@roc/feature-loan-fasttrack';
import { ApiResponse, GlobalStore } from '@roc/feature-app-core';

export class TamariskPaymentsStore {
  private globalStore: GlobalStore;
  private loanTermsService: LoanTermsService;
  public tamariskDocumentStore: TamariskDocumentStore;
  docSignStatus: boolean = false;
  paymentStatus: boolean = false;
  tamariskDocumentUrl: string = null;
  amcPaymentLink: string = "";

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.loanTermsService = new LoanTermsService();
    this.tamariskDocumentStore = new TamariskDocumentStore(globalStore);
    makeObservable(this, {
      docSignStatus: observable,
      fetchDocusignAndPaymentStatus: flow,
      fetchTamariskDocument: flow,
      paymentStatus: observable,
      setDocSignStatus: action,
      setTamariskDocumentUrl: action,
      tamariskDocumentUrl: observable,
      reset: action,
      persistDataAfterDocusignComplete: flow,
      getPropertyAdresses: action,
      generateAppraisalPaymentDocument: flow,
      amcPaymentLink: observable,
      fetchPaymentLink: flow,
    });
  }

  getPropertyAdresses(): string {
    if (!this.tamariskDocumentStore.properties) {
      return '';
    }
    return this.tamariskDocumentStore.properties
      .map(property => property.address)
      .join('; ');
  }

  setDocSignStatus(status: boolean) {
    this.docSignStatus = status;
  }

  setTamariskDocumentUrl(url: string) {
    this.tamariskDocumentUrl = url;
  }

  *fetchDocusignAndPaymentStatus(entityId: string, type: string) {
    try {
      const response: ApiResponse = yield this.loanTermsService.getAppraisalPaymentStatus(
        entityId,
        type
      );
      const { data } = response;

      if (data) {
        this.paymentStatus = data.paymentStatus;
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving payment status',
      });
    }
  }

  *generateAppraisalPaymentDocument(signatureDataURL: string) {
    try {
      const data = {
        loanId: this.tamariskDocumentStore.loanId,
        propertyAppraisalId: this.tamariskDocumentStore.propertyAppraisalId,
        cardHolderName: this.tamariskDocumentStore.form.fields.fullName.value,
        signature: signatureDataURL,
        userAgent: navigator.userAgent,
        address: this.getPropertyAdresses(),
      };
      const response: ApiResponse = yield this.loanTermsService.generateAppraisalPaymentDocument(
        data
      );

      if (!response.data.data) {
        return false;
      }

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Appraisal document signed successfully!',
      });
      this.setDocSignStatus(true);
      return true;
    } catch (error) {
      console.error('Error generating Appraisal document:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error signing Appraisal document. Please try again.',
      });
      return false;
    }
  }

  *fetchTamariskDocument(request: AppraisalRequest) {
    try {
      const response: ApiResponse = yield this.loanTermsService.getTamariskDocusign(
        request
      );
      const { data } = response;

      this.tamariskDocumentUrl = data ? data : null;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving acknowledgment document',
      });
    }
  }

  *persistDataAfterDocusignComplete(
    id: string,
    cardholderName: string,
    cardholderEmail: string,
    tamariskFlowType: string
  ) {
    const response: ApiResponse = yield this.loanTermsService.persistBorrowerData(
      id,
      cardholderName,
      cardholderEmail,
      tamariskFlowType
    );
  }

  reset() {
    this.docSignStatus = false;
    this.paymentStatus = false;
    this.tamariskDocumentUrl = null;
  }

  *fetchPaymentLink(loanId: string){
    try {
      const response: ApiResponse = yield this.loanTermsService.getAmcPayment(loanId);

      const { data } = response.data;

      this.amcPaymentLink = data ? data : null;
      console.log("AMCPAYMENT LINK:", this.amcPaymentLink);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while getting amc payment link',
      });
    }
  }
}

export default TamariskPaymentsStore;
